/**
 * Preferences API Service
 * Service layer for user preferences API operations
 * NOW USES CENTRALIZED apiService WITH AUTOMATIC AUTH
 */

import { UserPreferences, UserPreferencesResponse } from '@/types/user-preferences';
import { validatePreferences, mergeWithDefaults } from '@/lib/preferences-utils';
import { getErrorMessage } from '@/lib/error-handler';

// SSR-safe dynamic import for apiService
const getApiService = async () => {
  if (typeof window !== 'undefined') {
    const { apiService } = await import('@/services/api');
    return apiService;
  }
  return null;
};

export class PreferencesApiService {
  /**
   * Get user preferences from API
   */
  async getPreferences(): Promise<UserPreferences> {
    try {
      const apiService = await getApiService();
      if (!apiService) {
        throw new Error('apiService not available');
      }

      const response = await apiService.get('/admin/preferences');

      if (!response.success) {
        throw new Error(response.message || 'Failed to get preferences');
      }

      return response.data || mergeWithDefaults({});
    } catch (error) {
      const message = getErrorMessage(error) || 'Failed to fetch preferences';
      console.error('Error fetching preferences:', error);
      // Return default preferences on error
      return mergeWithDefaults({});
    }
  }

  /**
   * Update user preferences via API
   */
  async updatePreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    try {
      const apiService = await getApiService();
      if (!apiService) {
        throw new Error('apiService not available');
      }

      // Validate preferences before sending
      const validation = validatePreferences(preferences);
      if (!validation.isValid) {
        throw new Error(`Invalid preferences: ${validation.errors.join(', ')}`);
      }

      const response = await apiService.put('/admin/preferences', preferences);

      if (!response.success) {
        throw new Error(response.message || 'Failed to update preferences');
      }

      return response.data;
    } catch (error) {
      const message = getErrorMessage(error) || 'Failed to update preferences';
      console.error('Error updating preferences:', error);
      throw new Error(message);
    }
  }

  /**
   * Reset preferences to defaults
   */
  async resetPreferences(): Promise<UserPreferences> {
    const defaults = mergeWithDefaults({});
    return this.updatePreferences(defaults);
  }

  /**
   * Validate preferences without saving
   */
  validatePreferences(preferences: Partial<UserPreferences>): {
    isValid: boolean;
    errors: string[];
    validPreferences: Partial<UserPreferences>;
  } {
    return validatePreferences(preferences);
  }

  /**
   * Export preferences to JSON string
   */
  exportPreferences(preferences: UserPreferences): string {
    const exportData = {
      ...preferences,
      exportedAt: new Date().toISOString(),
      version: '1.0',
      source: 'joyous-admin-dashboard'
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import preferences from JSON string
   */
  importPreferences(jsonString: string): {
    success: boolean;
    preferences?: UserPreferences;
    error?: string;
  } {
    try {
      const imported = JSON.parse(jsonString);

      // Remove export metadata fields
      const { exportedAt, version, source, ...preferencesData } = imported;

      const validation = this.validatePreferences(preferencesData);

      if (validation.isValid) {
        const preferences = mergeWithDefaults(validation.validPreferences);
        return {
          success: true,
          preferences
        };
      } else {
        return {
          success: false,
          error: validation.errors.join(', ')
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Invalid JSON format'
      };
    }
  }

  /**
   * Check if API is available
   */
  async checkAvailability(): Promise<boolean> {
    try {
      const apiService = await getApiService();
      if (!apiService) {
        return false;
      }

      const response = await apiService.get('/preferences');
      return response.success;
    } catch (error) {
      console.warn('Preferences API not available:', error);
      return false;
    }
  }
}

// Create a singleton instance
export const preferencesApi = new PreferencesApiService();

export default PreferencesApiService;
